import type { NotificationData } from "@qendo/notifications";
import { Stack } from "expo-router";
import { useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { Button } from "~/components/Button";
import { HeaderButton } from "~/components/HeaderButton";
import { FilteredList } from "~/components/layout/FilteredList";
import { NotificationListingItem } from "~/components/layout/notification/NotificationListingItem";
import { Page } from "~/components/layout/Page";
import { Subtitle } from "~/components/layout/text";

import { Space } from "~/constants/sizing";
import { useDismiss } from "~/providers/DismissProvider";
import { useNotifications } from "~/providers/NotificationsProvider";

export default function Notifications() {
    const { handleDismiss } = useDismiss();
    const { markSeen, notifications, dismissSeen } = useNotifications();

    const groups = useMemo(
        () => [
            { name: "New", title: "New", matches: ({ seen }: NotificationData) => !seen },
            { name: "All", title: "All", matches: ({ seen }: NotificationData) => seen },
        ],
        [],
    );

    const markAllSeen = () => {
        for (const item of notifications) {
            if (!item.seen) {
                markSeen(item.identifier);
            }
        }
    };

    return (
        <>
            <Stack.Screen
                options={{
                    title: "Notifications",
                    headerLeft: () => (
                        <HeaderButton onPress={handleDismiss} title="Cancel" variant="destructive-quiet" />
                    ),
                    headerRight: () => <HeaderButton href="/more/account/notifications" icon="cog" />,
                }}
            />
            <Page spacer scrollable={false}>
                <FilteredList<NotificationData>
                    data={notifications}
                    groups={groups}
                    keyExtractor={(item) => item.identifier}
                    renderItem={({ item }) => <NotificationListingItem item={item} />}
                    ListEmptyComponent={() => <Subtitle>No notifications</Subtitle>}
                    ItemSeparatorComponent={() => <View style={{ height: Space.sm }} />}
                    sectionListProps={{
                        renderSectionHeader: ({ section }) => (
                            <View style={styles.sectionHeader}>
                                <Subtitle>{section.title}</Subtitle>
                                {section.title === "New" ? (
                                    <Button title="Mark all as read" variant="quiet" size="sm" onPress={markAllSeen} />
                                ) : (
                                    <Button title="Delete all read" variant="destructive-quiet" onPress={dismissSeen} />
                                )}
                            </View>
                        ),
                    }}
                    withoutSearch
                    withoutTabs
                />
            </Page>
        </>
    );
}

const styles = StyleSheet.create({
    sectionHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingVertical: Space.sm,
    },
});
